import { useEffect, useRef, useState } from 'react';
import { toUploadUrl } from '../../uploadUrl';

// Data-URL cache keyed by category/filename. The hooks are called from
// several places (one per Download button) and we don't want each one
// re-fetching the same image off the backend.
const cache = new Map<string, Promise<string | null>>();

async function fetchUploadAsDataUrl(category: string, filename: string): Promise<string | null> {
  const key = `${category}/${filename}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const promise = (async () => {
    try {
      const res = await fetch(toUploadUrl(category, filename), { credentials: 'omit' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  })();
  cache.set(key, promise);
  return promise;
}

/**
 * An uploaded image (school logo, person photo) as a data URL, for the PDF
 * renderer — which then never hits the uploads host (avoids CORS flakes on
 * remote PDFs). A missing or unreadable image resolves to null and the PDF
 * draws its placeholder.
 *
 * `ready` is false while it loads so callers can hold their Download
 * button — in practice the fetch finishes long before anyone clicks.
 */
export function useUploadDataUrl(category: string, filename: string | null | undefined): { dataUrl: string | null; ready: boolean } {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!filename) {
      setDataUrl(null);
      setReady(true);
      return;
    }
    setReady(false);
    fetchUploadAsDataUrl(category, filename).then((data) => {
      if (!mounted.current) return;
      setDataUrl(data);
      setReady(true);
    });
  }, [category, filename]);

  return { dataUrl, ready };
}

/** Profile photos keyed by filename, as data URLs — what the admit card and
 *  filled application print. Missing photos are simply absent. */
export type PhotoDataUrls = Record<string, string | null>;

/** Photos fetched and redrawn at a time when shrinking for print — enough
 *  to keep the network busy without decoding hundreds of originals at once. */
const PRINT_BATCH = 6;

/**
 * An image redrawn at most `maxPx` on its longer side, as a JPEG on white.
 * A photo uploaded from a phone can be 3 MB; on a 2 cm print it needs a
 * few tens of KB, and a PDF embeds every copy separately.
 */
export async function shrinkForPrint(source: Blob | string, maxPx: number, quality = 0.85): Promise<string | null> {
  try {
    const blob = typeof source === 'string' ? await (await fetch(source)).blob() : source;
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    return null;
  }
}

/**
 * Fetch the photos of these filenames (duplicates and blanks skipped) for a
 * PDF. With `maxPx`, each is shrunk for print as it arrives, a few at a time,
 * and the originals are not kept — use it for bulk prints.
 */
export async function fetchPhotoDataUrls(filenames: Array<string | null | undefined>, opts: { maxPx?: number } = {}): Promise<PhotoDataUrls> {
  const unique = Array.from(new Set(filenames.filter((f): f is string => !!f)));
  if (!opts.maxPx) {
    const urls = await Promise.all(unique.map(f => fetchUploadAsDataUrl('people', f)));
    return Object.fromEntries(unique.map((f, i) => [f, urls[i]]));
  }
  const out: PhotoDataUrls = {};
  for (let i = 0; i < unique.length; i += PRINT_BATCH) {
    await Promise.all(unique.slice(i, i + PRINT_BATCH).map(async f => {
      try {
        const res = await fetch(toUploadUrl('people', f), { credentials: 'omit' });
        out[f] = res.ok ? await shrinkForPrint(await res.blob(), opts.maxPx!) : null;
      } catch {
        out[f] = null;
      }
    }));
  }
  return out;
}
